/**
 * FormValidationSummary
 * ----------------------
 * A compact inline panel that surfaces the current `react-hook-form` error
 * state above the submit area, so users can see WHY the submit button is
 * disabled instead of having to guess.
 *
 * Reads `formState.errors` from the surrounding `useFormContext()` and
 * auto-hides when there are none. Must be rendered inside a `<Form />`
 * (a.k.a. `FormProvider`) tree.
 *
 * Props:
 * - `heading?: string`
 *   Optional heading text. Pass `translate('common.form.fixTheFollowing')`
 *   (or your equivalent key) to localize. When omitted, no heading renders —
 *   the component never ships a hardcoded English string.
 *
 * - `fieldLabels?: Record<string, string>`
 *   Optional map of field name → human label. When provided, the bullet
 *   shows "Label: message"; otherwise the raw field name is shown.
 *
 * - `className?: string`
 *   Optional extra classes appended to the root.
 *
 * Example:
 *   <FormValidationSummary
 *     heading={translate('common.form.fixTheFollowing')}
 *     fieldLabels={{
 *       firstname: translate('profile.form.firstname'),
 *       lastname: translate('profile.form.lastname'),
 *     }}
 *   />
 */

import * as React from 'react';
import { type FieldErrors, useFormContext } from 'react-hook-form';

import { cn } from '@/lib/utils';

type Props = {
  heading?: string;
  fieldLabels?: Record<string, string>;
  className?: string;
};

type FlatError = {
  path: string;
  message: string;
};

// Walks the (possibly nested) RHF errors object and flattens it into a list
// of `{ path, message }` rows. We use dotted paths so consumers can match on
// either the top-level field name or the full path.
function flattenErrors(
  errors: FieldErrors,
  prefix = '',
): FlatError[] {
  const out: FlatError[] = [];
  for (const key of Object.keys(errors)) {
    const node = (errors as Record<string, unknown>)[key];
    if (node === undefined || node === null) {
      continue;
    }
    const path = prefix === '' ? key : `${prefix}.${key}`;
    const message = (node as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) {
      out.push({ path, message });
      continue;
    }
    // No message on this node — recurse into nested FieldErrors (arrays /
    // nested objects). `type` is the RHF marker for a leaf with no message.
    if (typeof node === 'object' && ('type' in node) === false) {
      out.push(...flattenErrors(node as FieldErrors, path));
    }
  }
  return out;
}

function FormValidationSummary({
  heading,
  fieldLabels,
  className,
}: Props) {
  const hasHeading = heading !== undefined && heading.length > 0;
  const { formState } = useFormContext();
  const flat = React.useMemo(
    () => flattenErrors(formState.errors),
    [formState.errors],
  );

  if (flat.length === 0) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'bg-error-container/40 text-on-error-container rounded-md px-3 py-2 type-body-s',
        className,
      )}
    >
      {hasHeading === true && <p className="font-medium">{heading}</p>}
      <ul className={cn('list-disc space-y-0.5 pl-5', hasHeading === true && 'mt-1')}>
        {flat.map((entry) => {
          const label = fieldLabels?.[entry.path] ?? fieldLabels?.[entry.path.split('.')[0] ?? ''];
          return (
            <li key={entry.path}>
              {label !== undefined && label.length > 0
                ? (
                  <>
                    <span className="font-medium">{label}</span>
                    {': '}
                    {entry.message}
                  </>
                  )
                : entry.message}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export { FormValidationSummary };
